import { useState, useEffect, useMemo, useRef } from 'react'

import { isEmpty, omit } from 'lodash'
import { useSnackbar } from 'notistack'
import { useQuery, useMutation } from 'react-query'
import { useLocation, useHistory } from 'react-router-dom'

import { BeheardReportComponent } from './components'
import { API } from 'core/requests'
import { ROUTES_PATHS } from 'core/routes'
import { useLocale, useShoppingCartItemsCount } from 'contexts'
import { t } from 'i18n'

function useURLQuery() {
  return new URLSearchParams(useLocation().search)
}

export const getSurveyReportQueryParams = filters => {
  const newFilters: any = { criteria: [] }

  Object.keys(omit(filters, 'roles')).forEach(key => {
    if (filters[key].length) {
      newFilters.criteria.push({
        category: +key,
        demographics: filters[key],
      })
    }
  })

  if (filters.roles) {
    newFilters.roles = filters.roles
  }

  return newFilters
}

let filtersQuantity = 0

export default function BeheardReport() {
  const initialBeheardReport: any = {}
  const [beheardReport, setBeheardReport] = useState(initialBeheardReport)

  const [filtersState, setFiltersState] = useState({})
  const [filtersQuantityState, setFiltersQuantityState] = useState(0)
  const [processingState, setProcessingState] = useState<any>(null)
  const lastRequestBodyRef = useRef<any>(null)
  const [forceReset, setForceReset] = useState(0)

  const [minimumParticipantsAlert, setMinimumParticipantsAlert] =
    useState(false)
  const { setShoppingCartItemsCount } = useShoppingCartItemsCount()!

  useEffect(() => {
    filtersQuantity = 0

    if (!isEmpty(filtersState)) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      for (const [_, value] of Object.entries(filtersState)) {
        // @ts-ignore
        filtersQuantity += value.length
      }
    }

    setFiltersQuantityState(filtersQuantity)
  }, [filtersState])

  const history = useHistory()
  const location = useLocation()
  const query = useURLQuery()
  const { APILanguageChanged, setAPILanguageChanged } = useLocale()!

  const { enqueueSnackbar } = useSnackbar()

  const isFullReport = query.get('full_report')
  const companyIdFromQuery = query.get('company_id')

  const parsedFiltersFromURL = useMemo(() => {
    const roles: any[] = []
    const criteriaMap: Record<
      string,
      { category?: number; demographics: any[] }
    > = {}
    let autoCounter = -1
    let lastAutoKey: string | null = null

    query.forEach((value, key) => {
      if (key === 'roles' || key === 'roles[]') {
        roles.push(isNaN(Number(value)) ? value : Number(value))
        return
      }

      const criteriaMatch =
        key.match(/^criteria\[(\d*)\]\[(category|demographics)\]$/) ||
        key.match(/^criteria\[(\d*)\]\[(demographics)\]\[\]$/)

      if (criteriaMatch) {
        const [, idxRaw, field] = criteriaMatch

        let idx: string
        if (idxRaw === '') {
          if (field === 'category') {
            autoCounter += 1
            lastAutoKey = `auto-${autoCounter}`
            idx = lastAutoKey
          } else {
            // demographics without explicit index — attach to last category
            if (!lastAutoKey) {
              autoCounter += 1
              lastAutoKey = `auto-${autoCounter}`
            }
            idx = lastAutoKey
          }
        } else {
          idx = idxRaw
          lastAutoKey = idx
        }

        criteriaMap[idx] = criteriaMap[idx] || { demographics: [] }

        if (field === 'category') {
          criteriaMap[idx].category = Number(value)
        } else {
          criteriaMap[idx].demographics.push(
            isNaN(Number(value)) ? value : Number(value),
          )
        }
      }
    })

    const criteria = Object.values(criteriaMap).filter(c => c.category)

    return { roles, criteria }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, forceReset])

  const hasFilterParamsInURL = useMemo(() => {
    const params = new URLSearchParams(location.search)
    for (const key of params.keys()) {
      if (key === 'roles' || key === 'roles[]' || key.startsWith('criteria[')) {
        return true
      }
    }
    return false
  }, [location.search])

  useEffect(
    () => {
      const showPreviousReportVisual =
        query.get('id') == '369' || query.get('id') == '844' ? 'true' : 'false'
      localStorage.setItem('showPreviousReportVisual', showPreviousReportVisual)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  const addSurveyToCartMutation = useMutation(
    (payload: any) => API.create('shoppingCartAddItem', payload),
    {
      onSettled: (response: any) => {
        if ((response && response.id) || (response && response.success)) {
          enqueueSnackbar(
            t('beheardReport.snackbar.success.surveyAddedToCart'),
            {
              variant: 'success',
            },
          )
          setShoppingCartItemsCount(state => state + 1)
          history.push(ROUTES_PATHS.marketing)
        }
      },
    },
  )

  const addSelectedSurveyToCart = () => {
    addSurveyToCartMutation.mutate({
      product_key: 'full_report',
      object_name_to_upgrade: 'Survey',
      object_id_to_upgrade:
        beheardReport && beheardReport.survey && beheardReport.survey.id,
    })
  }

  const surveyReportFromAPIMutation = useMutation(
    (payload: any) => {
      if (+payload.isFullReport) {
        return API.create('surveyFullReport', payload.body, {
          pathParams: {
            id: payload.id,
          },
        })
      }

      return API.create('surveyReport', payload.queryParams, {
        pathParams: {
          id: payload.id,
          report_type: 'free_report',
        },
      })
    },
    {
      onSuccess: response => {
        if (response?.status === 'processing') {
          setProcessingState({
            message: response.message,
            jobId: response.job_id,
            filters: response.filters,
          })

          if (isEmpty(demographics)) {
            fetchDemographics()
          }

          return
        }

        setProcessingState(null)
        setBeheardReport({ ...response })

        if (isEmpty(demographics)) {
          fetchDemographics()
        }
      },
      onSettled: (response: any) => {
        if (response?.error_type === 'minimum_participants_needed') {
          setMinimumParticipantsAlert(true)
        } else if (response && response.errors) {
          history.push(ROUTES_PATHS.surveyOverview)
        } else if (filtersQuantityState && response?.status !== 'processing') {
          enqueueSnackbar(t('beheardReport.snackbar.success.filters'), {
            variant: 'success',
          })
        }

        if (
          (minimumParticipantsAlert && !filtersQuantityState) ||
          (minimumParticipantsAlert && response && !response.error_type)
        ) {
          setMinimumParticipantsAlert(false)
        }
      },
    },
  )

  const {
    data: demographics,
    isLoading: demographicsIsLoading,
    refetch: fetchDemographics,
  } = useQuery(
    'getDemographics',
    () => {
      const surveyId = query.get('id')

      return API.get('demographics', null, {
        queryParams: surveyId && {
          survey_id: surveyId,
        },
      })
    },
    {
      enabled: false,
    },
  )

  const { data: roles, refetch: fetchRoles } = useQuery(
    'getRoles',
    () => {
      return API.get('roles')
    },
    {
      enabled: true, // prefetch to avoid lag when opening filters
      staleTime: 5 * 60 * 1000,
    },
  )

  useEffect(() => {
    if (!isEmpty(demographics)) {
      const filters = {}
      demographics.forEach(demographic => {
        filters[demographic.id] = []
      })

      parsedFiltersFromURL.criteria?.forEach(filterCriteria => {
        if (filterCriteria.category != null && filters.hasOwnProperty(filterCriteria.category)) {
          filters[filterCriteria.category] = filterCriteria.demographics || []
        }
      })

      if (parsedFiltersFromURL.roles?.length) {
        filters['roles'] = parsedFiltersFromURL.roles
      }

      setFiltersState(filters)
      if (hasFilterParamsInURL) {
        handleBeheardReportFetch(filters)
      }
    }
  }, [demographics, parsedFiltersFromURL, hasFilterParamsInURL])

  // When arriving with filters in the URL, fetch demographics immediately so we can
  // translate category ids to filter state and trigger the first report request.
  useEffect(() => {
    if (hasFilterParamsInURL && +isFullReport && isEmpty(demographics)) {
      fetchDemographics()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasFilterParamsInURL, isFullReport])

  const handleBeheardReportFetch = (
    overrideFiltersState?: any,
    bodyOverride?: any,
  ) => {
    const filtersToUse = overrideFiltersState ?? filtersState
    const queryParams = bodyOverride ?? getSurveyReportQueryParams(filtersToUse)
    const body =
      companyIdFromQuery && +isFullReport
        ? { ...queryParams, company_id: companyIdFromQuery }
        : queryParams

    if (+isFullReport) {
      lastRequestBodyRef.current = body
    }

    surveyReportFromAPIMutation.mutate({
      id: query.get('id'),
      isFullReport: isFullReport,
      queryParams,
      body,
    })
  }

  useEffect(() => {
    if (!(hasFilterParamsInURL && +isFullReport)) {
      handleBeheardReportFetch()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!processingState) return

    const intervalId = setInterval(() => {
      const pollBody =
        (processingState.filters && {
          ...processingState.filters,
          ...(companyIdFromQuery ? { company_id: companyIdFromQuery } : {}),
        }) ||
        lastRequestBodyRef.current ||
        getSurveyReportQueryParams(filtersState)

      handleBeheardReportFetch(filtersState, pollBody)
    }, 15000)

    return () => clearInterval(intervalId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [processingState])

  useEffect(() => {
    if (APILanguageChanged) {
      handleBeheardReportFetch()
      setAPILanguageChanged(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [APILanguageChanged])

  return (
    <BeheardReportComponent
      beheardReport={beheardReport}
      beheardReportIsLoading={surveyReportFromAPIMutation.isLoading}
      isFullReport={isFullReport}
      addSurveyToCart={addSelectedSurveyToCart}
      addSurveyToCartIsLoading={addSurveyToCartMutation.isLoading}
      demographics={demographics}
      demographicsIsLoading={demographicsIsLoading}
      roles={roles}
      fetchRoles={fetchRoles}
      filtersState={filtersState}
      filtersQuantityState={filtersQuantityState}
      setFiltersState={setFiltersState}
      fetchSurveyReportFromAPI={handleBeheardReportFetch}
      surveyPermissionId={query.get('shared_report_id')}
      minimumParticipantsAlert={minimumParticipantsAlert}
      processingState={processingState}
    />
  )
}
